import { useEffect, useMemo, useState } from 'react';

import { css } from '@emotion/react';
import { Box, getCSSVariable, Text } from '@opendoor/bricks-next';

import { EntryComponent } from 'cms/entries/entries';

import { ILpComponentCountdownTimer } from 'declarations/contentful';

import Container from './shared/Container';

type TimeParts = {
  totalMs: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const TWO_DIGIT = (value: number): string => value.toString().padStart(2, '0');

function getTimeParts(endDateIso?: string): TimeParts {
  const end = endDateIso ? new Date(endDateIso).getTime() : Date.now();
  const now = Date.now();
  const diff = Math.max(0, end - now);

  const seconds = Math.floor((diff / 1000) % 60);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  return { totalMs: diff, days, hours, minutes, seconds };
}

const DigitTile = ({ digit }: { digit: string }) => (
  <Box
    aria-hidden
    width={24}
    height={44}
    $largerThanSM={{ width: 60, height: 90 }}
    backgroundColor="$backgroundAccentBrand"
    alignItems="center"
    justifyContent="center"
    display="inline-flex"
    borderRadius="$2x"
    css={css`
      box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.35), inset 1px 0 0 rgba(0, 0, 0, 0.05);
    `}
  >
    <Text
      tag="span"
      typography="$subheaderSmall"
      $largerThanSM={{ typography: '$headerLarge' }}
      fontWeight="$medium"
      color="white"
      css={css`
        font-variant-numeric: tabular-nums;
        font-feature-settings: 'tnum' 1, 'lnum' 1;
      `}
    >
      {digit}
    </Text>
  </Box>
);

const Unit = ({ label, value }: { label: string; value: number }) => {
  const digits = useMemo(() => TWO_DIGIT(Math.max(0, value)).split(''), [value]);
  return (
    <Box alignItems="center" gap={6}>
      <Box
        display="flex"
        flexDirection="row"
        gap={2}
        $largerThanSM={{ gap: 4 }}
        alignItems="center"
      >
        {digits.map((d, i) => (
          <DigitTile key={`${label}-${i}`} digit={d} />
        ))}
      </Box>
      <Text
        tag="span"
        typography="$labelXsmall"
        $largerThanSM={{ typography: '$labelMedium' }}
        color={getCSSVariable('$contentSecondary', 'color')}
      >
        {label}
      </Text>
    </Box>
  );
};

const Colon = () => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    height={44}
    $largerThanSM={{ height: 64 }}
  >
    <Text
      tag="span"
      typography="$headerSmall"
      $largerThanSM={{ typography: '$headerLarge' }}
      fontWeight="$medium"
      marginTop={-25}
    >
      :
    </Text>
  </Box>
);

const RenderCountdownTimer = (entry: ILpComponentCountdownTimer) => {
  const { endDate, title } = entry.fields;
  const [time, setTime] = useState<TimeParts>(() => getTimeParts(endDate));

  useEffect(() => {
    const id = setInterval(() => setTime(getTimeParts(endDate)), 1000);
    return () => clearInterval(id);
  }, [endDate]);

  return (
    <Container>
      {title && (
        <Box mb="$24x">
          <Text
            tag="span"
            display="block"
            typography="$displaySmall"
            $smallerThanMD={{ typography: '$headerMedium' }}
          >
            {title}
          </Text>
        </Box>
      )}
      <Box
        backgroundColor="$backgroundTertiary"
        borderRadius="$8x"
        p="$12x"
        $largerThanSM={{ p: '$20x' }}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        maxWidth={1100}
        width="100%"
        marginHorizontal="auto"
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={12}
          $largerThanSM={{ gap: 24 }}
        >
          <Unit label="days" value={time.days} />
          <Colon />
          <Unit label="hours" value={time.hours} />
          <Colon />
          <Unit label="minutes" value={time.minutes} />
          <Colon />
          <Unit label="seconds" value={time.seconds} />
        </Box>
      </Box>
    </Container>
  );
};

const CountdownTimer: EntryComponent<ILpComponentCountdownTimer> = {
  render: RenderCountdownTimer,
};

export default CountdownTimer;
